import { motion } from "framer-motion";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

/**
 * V3.1-only editorial ingredient module.
 *
 * Narrative slot: sits right after StoryDermatology (Dr. Rivers) and before
 * SkincareHighlights so shoppers meet the founder → then the founder's
 * signature ingredient → then the full active system. Mirrors the 47skin
 * "Our Unique Formula" beat but adapted to Riversol's quiet cream/paper
 * aesthetic instead of a dark hero surface.
 */
export const HinokitiolSection = () => {
  const { v32, v34, mediaRadius } = useProduct();
  const isV32 = !!v32;
  const outcomes = isV32
    ? [
        "Backed by 120 peer-reviewed studies",
        "A potent antioxidant that defends skin against daily environmental stress",
        "Stays gentle on skin that reacts to almost everything else",
        "The basis of almost every Riversol formulation",
      ]
    : [
        "Brightens dark spots",
        "Improves clarity",
        "Evens texture",
      ];

  return (
    <section
      className={`bg-riversol-paper ${isV32 ? "pt-20 pb-12 lg:pt-32 lg:pb-16" : "py-20 lg:py-32"}`}
      data-testid="hinokitiol-section"
    >
      <div className="max-w-[1500px] mx-auto px-4 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Content column — sits LEFT on desktop, stacks BELOW image on mobile.
            Mirrors StoryDermatology's 6/6 split and content-left / image-right
            layout so this section reads as a natural companion beat. */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <h2
            className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.06] tracking-tight font-semibold text-neutral-900 mb-3"
            data-testid="hinokitiol-title"
          >
            Hinokitiol
          </h2>
          {/*}
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-8">
            {isV32 ? "Thuja plicata" : "Chamaecyparis obtusa"} · Pacific red cedar
          </div>*/}

          <Reveal>
            <p className="text-[16px] lg:text-[17px] leading-[1.75] lg:leading-[1.8] text-neutral-600 max-w-xl mb-8">
              Our signature antioxidant, found in the Pacific red cedar,
              visibly brightens dark spots, improves skin clarity and evens
              skin texture.
            </p>
          </Reveal>

          <ul
            className="flex flex-col gap-2 mb-10 max-w-md"
            data-testid="hinokitiol-outcomes"
          >
            {outcomes.map((label, i) => (
              <li
                key={label}
                className="flex items-center gap-3 py-2 border-t border-neutral-200 last:border-b"
                data-testid={`hinokitiol-outcome-${i + 1}`}
              >
                {!v34 && (
                  <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 w-8">
                    0{i + 1}
                  </span>
                )}
                <span className="text-[15px] text-neutral-800">{label}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={(e) => e.preventDefault()}
            className={`inline-flex items-center justify-center gap-2 bg-riversol-blue text-white text-xs sm:text-sm tracking-[0.12em] uppercase font-medium py-3.5 sm:py-4 px-8 ${isV32 ? "" : "rounded-full"} hover:bg-riversol-blueLight transition-colors duration-200`}
            data-testid="hinokitiol-learn-more"
          >
            <span>Learn more</span>
            <span aria-hidden="true">→</span>
          </button>
        </div>

        {/* Editorial cedar photo — right column on desktop, top on mobile.
            Aspect-square matches the StoryDermatology image directly above so
            the two sections form a consistent editorial rhythm. */}
        <div className="lg:col-span-6 relative order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="editorial-frame bg-white group relative overflow-hidden aspect-square"
            data-testid="hinokitiol-media"
            style={{ clipPath: `inset(0 round ${mediaRadius || "15px"})` }}
          >
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_anti-aging-trio-pdp-1/artifacts/iwpm3rjf_hinokitiol.png"
              alt="Macro photograph of Pacific red cedar foliage — the natural source of Hinokitiol."
              className="w-full h-full object-cover img-zoom"
              loading="lazy"
            />
            <span
              className={`absolute bottom-6 left-6 lg:bottom-8 lg:left-8 ${isV32 ? "font-sans" : "font-serif"} italic text-white/95 text-lg lg:text-xl tracking-wide [text-shadow:_0_1px_12px_rgba(0,0,0,0.55)] select-none`}
              data-testid="hinokitiol-caption"
            >
              Signature antioxidant
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HinokitiolSection;
